import { useCallback } from "react";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import InputElement from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import CreativeSourceRequestResource from "common/resources/Project/Config/CreativeSource/CreativeSourceRequestResource";
import classes from "./classes.module.scss";

type IProps = {
	onClose: () => void;
	onCreate: (creativeSource: CreativeSourceRequestResource, image?: File) => void;
};

export default function CreateCreativeSourceModal(props: IProps) {
	const { formRef, errors, handleError, clearErrors } = useForm();

	const onCreateCreativeSource = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			const creativeSourceRequestResource = CreativeSourceRequestResource.hydrate<CreativeSourceRequestResource>({
				description: [
					{
						lang: I18n.getLang(),
						text: formData["description"] as string,
					},
				],
			});

			const image = (formData["image"] as FileList)?.[0];

			creativeSourceRequestResource
				.validateOrReject()
				.then(() => props.onCreate(creativeSourceRequestResource, image))
				.catch(handleError);
		},
		[clearErrors, handleError, props],
	);

	return (
		<Modal isOpen onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onCreateCreativeSource} errors={errors} formRef={formRef} className={classes["form-container"]}>
				<div className={classes["inputs-container"]}>
					<DragAndDropFile
						name="image"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.image}
						description={I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.image_description}
						formRef={formRef}
						className={classes["drag-and-drop"]}
					/>
					<InputElement
						name="description"
						required
						additionalInfo={I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.description_additional_info}
						label={I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.description}
						className={classes["input"]}
					/>
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.cancel}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
						{I18n.asset.pages.professional_space.tabs.information.creative_source.create_creative_source_modal.create}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
